import base from "lib/base";
import { getWebInfoNoCache } from "lib/getFetchers";
import { languageBuild } from "lib/language";
import { searchEngineRobots } from "lib/searchEngineSeo";

const DEFAULT_TITLE = "Gerelt";
const DEFAULT_DESCRIPTION = "Gerelt";
const BRAND_NAME = "Gerelt";

const safeTrim = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

const sanitizeBrand = (value, fallback = BRAND_NAME) => {
  if (!value) return fallback;
  const raw = String(value).trim();
  if (!raw) return fallback;

  const cleaned = raw
    .replace(/\s*[-|]\s*gerelt\s*publish\.com/gi, "")
    .replace(/gerelt\s*publish\.com/gi, "")
    .trim();

  return cleaned || fallback;
};

const resolveAssetUrl = (value) => {
  if (!value) return null;
  const str = String(value).trim();
  if (!str) return null;
  if (/^https?:\/\//i.test(str)) return str;
  if (str.startsWith("/")) return `${base.baseUrl}${str}`;
  return `${base.cdnUrl}/${str.replace(/^\/+/, "")}`;
};

export async function getSiteSeoConfig(lang = "mn") {
  const { info } = await getWebInfoNoCache(lang);
  const resolved = Array.isArray(info) ? info[0] || {} : info || {};
  const core = resolved?.core || resolved || {};
  const translation = resolved?.translation || {};
  const seo = translation?.seo || core?.seo || {};

  const brandName = sanitizeBrand(
    languageBuild(resolved, "siteTitle", lang) ||
      translation?.siteTitle ||
      core?.siteTitle ||
      DEFAULT_TITLE,
  );

  const title = sanitizeBrand(safeTrim(seo?.metaTitle) || brandName, brandName);

  const description = sanitizeBrand(
    safeTrim(
      seo?.metaDescription ||
        languageBuild(resolved, "shortDescription", lang) ||
        languageBuild(resolved, "siteShortInfo", lang) ||
        translation?.shortDescription ||
        core?.shortDescription ||
        DEFAULT_DESCRIPTION,
    ),
    DEFAULT_DESCRIPTION,
  );

  const ogImageRaw =
    seo?.ogImage ||
    languageBuild(resolved, "whiteLogo", lang) ||
    translation?.whiteLogo ||
    translation?.logo ||
    core?.logo ||
    core?.aboutPicture;

  const imageUrl = resolveAssetUrl(ogImageRaw) || `${base.baseUrl}/assets/img/about_img.png`;
  const twitterImage =
    resolveAssetUrl(seo?.twitterImage || seo?.ogImage || ogImageRaw) || imageUrl;

  const rawFavicon =
    translation?.favicon || core?.favicon || resolved?.favicon || null;
  const faviconUrl = rawFavicon ? resolveAssetUrl(rawFavicon) : `${base.baseUrl}/favicon.ico`;
  const faviconVersion =
    translation?.updatedAt ||
    core?.updatedAt ||
    resolved?.updatedAt ||
    translation?.faviconVersion ||
    core?.faviconVersion;

  const metadataBaseUrl = safeTrim(core?.siteUrl || base.baseUrl) || base.baseUrl;
  const canonicalUrl = safeTrim(seo?.canonicalUrl || metadataBaseUrl) || metadataBaseUrl;

  const ogTitle = sanitizeBrand(safeTrim(seo?.ogTitle) || title, brandName);
  const ogDescription = sanitizeBrand(
    safeTrim(seo?.ogDescription) || description,
    DEFAULT_DESCRIPTION,
  );

  const twitterTitle = sanitizeBrand(
    safeTrim(seo?.twitterTitle) || safeTrim(seo?.ogTitle) || title,
    brandName,
  );
  const twitterDescription = sanitizeBrand(
    safeTrim(seo?.twitterDescription) ||
      safeTrim(seo?.ogDescription) ||
      description,
    DEFAULT_DESCRIPTION,
  );

  return {
    brandName,
    title,
    description,
    metadataBaseUrl,
    canonicalUrl,
    ogTitle,
    ogDescription,
    twitterTitle,
    twitterDescription,
    imageUrl,
    twitterImage,
    faviconUrl,
    faviconVersion,
    ogType: seo?.ogType || "website",
    twitterCard: seo?.twitterCard || "summary_large_image",
    keywords: Array.isArray(seo?.metaKeywords)
      ? seo.metaKeywords.filter(Boolean)
      : undefined,
    robots: searchEngineRobots,
  };
}

/** Root layout: зөвхөн суурь — openGraph/twitter/canonical БҮГДИЙГ child өгнө */
export async function buildRootLayoutMetadata() {
  const site = await getSiteSeoConfig();

  return {
    metadataBase: new URL(site.metadataBaseUrl),
    title: {
      default: site.title,
      template: `%s | ${site.brandName}`,
    },
    description: site.description,
    keywords: site.keywords,
    robots: site.robots,
    icons: site.faviconUrl
      ? {
          icon: site.faviconVersion
            ? `${site.faviconUrl}?v=${site.faviconVersion}`
            : site.faviconUrl,
          shortcut: site.faviconVersion
            ? `${site.faviconUrl}?v=${site.faviconVersion}`
            : site.faviconUrl,
          apple: site.faviconVersion
            ? `${site.faviconUrl}?v=${site.faviconVersion}`
            : site.faviconUrl,
        }
      : undefined,
  };
}

/** Нүүр хуудас болон бусад marketing хуудсын бүрэн social preview */
export async function buildSiteSocialMetadata(overrides = {}) {
  const site = await getSiteSeoConfig();
  const pageUrl = overrides.url || site.canonicalUrl;
  const pageTitle = overrides.title || site.ogTitle;
  const pageDescription = overrides.description || site.ogDescription;
  const imageUrl = overrides.imageUrl || site.imageUrl;

  return {
    metadataBase: new URL(site.metadataBaseUrl),
    title: overrides.title || site.title,
    description: pageDescription,
    robots: overrides.robots ?? searchEngineRobots,
    alternates: pageUrl ? { canonical: pageUrl } : undefined,
    openGraph: {
      title: pageTitle,
      description: pageDescription,
      url: pageUrl,
      siteName: site.brandName,
      locale: "mn_MN",
      type: overrides.type || site.ogType,
      images: imageUrl
        ? [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: pageTitle || site.brandName,
            },
          ]
        : [],
    },
    twitter: {
      card: site.twitterCard,
      title: overrides.twitterTitle || site.twitterTitle,
      description: overrides.twitterDescription || site.twitterDescription,
      images: imageUrl ? [imageUrl] : [],
      site: "@gerelt",
      creator: "@gerelt",
    },
  };
}
